import * as real from "src/pages/study/sites/sites.service"
import * as mock from "src/pages/study/sites/sites.mock"
import { queryOptions } from "@tanstack/react-query"
import { CACHE_TIME } from "src/pages/study/constants"
import { QueryOptionsData } from "@myvp/shared/src/types"
import { SiteSignatureDataSource } from "src/gql/graphql"
import { selectService } from "@myvp/shared/src/functions/mock"
import { roundCompliance } from "src/pages/study/functions/format-compliance"

const service =
  process.env.REACT_APP_release === "true"
    ? real
    : selectService({ mock, real })
export const listSiteMetricsOptions = (args: {
  globalSponsorStudyId: string
}) => {
  return queryOptions({
    queryKey: ["listSiteMetrics", args.globalSponsorStudyId],
    queryFn: async () => {
      const result = await service.listSiteMetrics(args.globalSponsorStudyId)
      return {
        ...result,
        siteSummaryMetrics: result.siteSummaryMetrics.map((m) => ({
          ...m,
          eproCompliance:
            typeof m.eproCompliance === "number"
              ? roundCompliance(m.eproCompliance)
              : m.eproCompliance,
          eclinroCompliance:
            typeof m.eclinroCompliance === "number"
              ? roundCompliance(m.eclinroCompliance)
              : m.eclinroCompliance,
        })),
      }
    },
    staleTime: CACHE_TIME,
    gcTime: CACHE_TIME,
  })
}

const modelParticipantSignatureMetricsByDataSource = (args: {
  serverState: Awaited<
    ReturnType<typeof service.getSiteAndParticipantMetrics>
  >["siteParticipantMetrics"]["participantSignatureMetrics"]
}) => {
  const signatureMetricsByDataSource: Record<
    SiteSignatureDataSource | "all",
    {
      studyParticipantId: string
      participantNumber: string
      percentSurveysSigned?: number | null
      countSurveysSigned: number
      countSurveysRequiringSignature: number
    }[]
  > = {
    all: [],
    [SiteSignatureDataSource.Transcription]: [],
    [SiteSignatureDataSource.SourceEpro]: [],
    [SiteSignatureDataSource.SourceEclinro]: [],
  }
  for (const {
    signatureMetricsByDataSource: metricsForParticipant,
    ...participant
  } of args.serverState) {
    for (const { dataSource, ...metrics } of metricsForParticipant) {
      const participantMetrics = {
        ...participant,
        ...metrics,
      }
      switch (dataSource) {
        case SiteSignatureDataSource.Transcription:
        case SiteSignatureDataSource.SourceEpro:
        case SiteSignatureDataSource.SourceEclinro:
          signatureMetricsByDataSource[dataSource].push(participantMetrics)
          break
        case null:
        case undefined:
          signatureMetricsByDataSource.all.push(participantMetrics)
          break
      }
    }
  }
  return signatureMetricsByDataSource
}

export const getSiteAndParticipantMetricsOptions = (args: {
  globalSponsorStudyId: string
  siteId: string
}) => {
  return queryOptions({
    queryKey: [
      "getSiteAndParticipantMetrics",
      args.globalSponsorStudyId,
      args.siteId,
    ],
    queryFn: async () => {
      const result = await service.getSiteAndParticipantMetrics({
        globalSponsorStudyId: args.globalSponsorStudyId,
        siteId: args.siteId,
      })

      const hasNeitherEproNorEclinro =
        !result.siteParticipantMetrics.siteSummaryMetrics.hasEpro &&
        !result.siteParticipantMetrics.siteSummaryMetrics.hasEclinro

      return {
        ...result,
        siteParticipantMetrics: {
          ...result.siteParticipantMetrics,
          eproParticipantMetrics:
            result.siteParticipantMetrics.eproParticipantMetrics.map((p) => ({
              ...p,
              completedSurveysPercent:
                typeof p.completedSurveysPercent === "number"
                  ? roundCompliance(p.completedSurveysPercent)
                  : p.completedSurveysPercent,
            })),
          eclinroParticipantMetrics:
            result.siteParticipantMetrics.eclinroParticipantMetrics.map(
              (p) => ({
                ...p,
                completedSurveysPercent:
                  typeof p.completedSurveysPercent === "number"
                    ? roundCompliance(p.completedSurveysPercent)
                    : p.completedSurveysPercent,
              })
            ),
          participantSignatureMetrics:
            modelParticipantSignatureMetricsByDataSource({
              serverState:
                result.siteParticipantMetrics.participantSignatureMetrics,
            }),
          siteSummaryMetrics: {
            ...result.siteParticipantMetrics.siteSummaryMetrics,
            eproCompliance:
              typeof result.siteParticipantMetrics.siteSummaryMetrics
                .eproCompliance === "number"
                ? roundCompliance(
                    result.siteParticipantMetrics.siteSummaryMetrics
                      .eproCompliance
                  )
                : result.siteParticipantMetrics.siteSummaryMetrics
                    .eproCompliance,
            eclinroCompliance:
              typeof result.siteParticipantMetrics.siteSummaryMetrics
                .eclinroCompliance === "number"
                ? roundCompliance(
                    result.siteParticipantMetrics.siteSummaryMetrics
                      .eclinroCompliance
                  )
                : result.siteParticipantMetrics.siteSummaryMetrics
                    .eclinroCompliance,
            ...(hasNeitherEproNorEclinro
              ? {
                  hasEpro: true,
                  hasEclinro: true,
                }
              : {}),
          },
        },
      }
    },
    staleTime: CACHE_TIME,
    gcTime: CACHE_TIME,
  })
}

export type SiteAndSignatureParticipantMetrics = QueryOptionsData<
  typeof getSiteAndParticipantMetricsOptions
>
